import ReactGA from "react-ga4";

// Google Analytics 4 with Consent Mode v2.
//
// Consent defaults are set to "denied" in public/index.html BEFORE gtag.js
// loads. We then initialise GA for EVERY visitor (initAnalytics) so that, while
// consent is denied, GA sends only cookieless, anonymised pings that Google uses
// for modelled reporting. When the visitor accepts the cookie banner we flip
// analytics_storage to "granted" (grantAnalyticsConsent) and it becomes full,
// cookie-based analytics. Declining keeps it cookieless — no personal data, no
// identifiers stored — which is what keeps us GDPR/UK-GDPR compliant.
//
// GA runs only when: REACT_APP_GA_MEASUREMENT_ID is set AND it's a production
// build (or REACT_APP_GA_DEBUG=true). Every helper is a safe no-op otherwise.

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const ENV_ALLOWED =
  !!GA_ID &&
  (process.env.NODE_ENV === "production" || process.env.REACT_APP_GA_DEBUG === "true");

let initialized = false;
let lastPagePath = null;

// Push to the same dataLayer gtag.js consumes (mirrors the gtag() shim in
// index.html). Used for Consent Mode updates.
function gtag() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

export function analyticsEnabled() {
  return initialized;
}

// Load GA for all visitors (Consent Mode handles the cookieless-vs-full split).
// Skipped during static prerendering so no GA tag is baked into the HTML.
export function initAnalytics() {
  if (!ENV_ALLOWED || initialized) return;
  if (typeof window !== "undefined" && window.__PRERENDER__) return;
  // send_page_view:false — pageviews are sent explicitly (initial load here,
  // route changes via AnalyticsTracker) so nothing is double-counted.
  ReactGA.initialize(GA_ID, { gtagOptions: { send_page_view: false } });
  initialized = true;
  trackPageView(window.location.pathname + window.location.search);
}

// Called by CookieConsent when consent is accepted. Analytics is granted by
// default (see index.html), so this is usually a no-op — it only matters if the
// visitor had previously declined and is now opting back in.
export function grantAnalyticsConsent() {
  if (!ENV_ALLOWED) return;
  gtag("consent", "update", { analytics_storage: "granted" });
}

// Called by CookieConsent on decline — opt out. Future hits become cookieless
// (no identifiers stored on the device) for visitors who explicitly decline.
export function denyAnalyticsConsent() {
  if (!ENV_ALLOWED) return;
  gtag("consent", "update", { analytics_storage: "denied" });
}

export function trackPageView(path) {
  if (!initialized || !path || path === lastPagePath) return;
  lastPagePath = path;
  ReactGA.send({ hitType: "pageview", page: path });
}

export function trackEvent({ category, action, label, value }) {
  if (!initialized || !action) return;
  ReactGA.event({ category, action, label, value });
}

export function setUserProperties(properties) {
  if (!initialized || !properties) return;
  ReactGA.gtag("set", "user_properties", properties);
}
